import type { FC, SVGProps } from "react";
import KkScrewGraphic, { type KkScrewSize, type MillimetersToPixels } from "./KkScrew";

type TvmProps = SVGProps<SVGSVGElement> & {
  screw?: Partial<KkScrewSize> | null;
  // How far the connector's base foot reaches below its arm - varies per connector (its minimum
  // grooving height), unlike the rest of the profile which is a fixed stamped shape.
  totalHeight?: number;
};

export const TVM_CONNECTOR_WIDTH_MM = 31;
export const TVM_CONNECTOR_TOTAL_HEIGHT_MM = 8.9;
export const TVM_CONNECTOR_BASE_WIDTH_MM = 12;
export const TVM_CONNECTOR_PROFILE_THICKNESS_MM = 1.5;
export const TVM_CONNECTOR_ARM_TOP_THICKNESS_MM = 0.6;
export const TVM_CONNECTOR_BASE_GAP_CLEARANCE_MM = 2;
export const TVM_CONNECTOR_GROOVE_DEPTH_CLEARANCE_MM = 2;
export const TVM_CONNECTOR_ARM_SIDE_LENGTH_MM = (TVM_CONNECTOR_WIDTH_MM - TVM_CONNECTOR_BASE_WIDTH_MM) / 2;

const BODY_COLOR = "#b8b8b8";
const STROKE_COLOR = "#111";
const PROFILE_OUTLINE_WIDTH = TVM_CONNECTOR_PROFILE_THICKNESS_MM + 0.35;
const FLANGE_STROKE_WIDTH = 0.14;
const millimetersToGraphicUnits: MillimetersToPixels = (millimeters) => millimeters;

const Tvm: FC<TvmProps> = ({ screw, totalHeight = TVM_CONNECTOR_TOTAL_HEIGHT_MM, ...props }) => {
  const centerX = TVM_CONNECTOR_WIDTH_MM / 2;
  const baseLeftX = centerX - TVM_CONNECTOR_BASE_WIDTH_MM / 2;
  const baseRightX = centerX + TVM_CONNECTOR_BASE_WIDTH_MM / 2;
  const topY = TVM_CONNECTOR_ARM_TOP_THICKNESS_MM + TVM_CONNECTOR_PROFILE_THICKNESS_MM / 2;
  const bendY = topY + 2.3;
  // ponytail: floor keeps the bend/base geometry from inverting for implausibly small heights;
  // raise if a connector with a smaller minimum grooving height ever ships.
  const resolvedTotalHeight = Math.max(totalHeight, bendY + 1.4);
  const baseY = resolvedTotalHeight - TVM_CONNECTOR_PROFILE_THICKNESS_MM / 2;
  const baseTopY = baseY - TVM_CONNECTOR_PROFILE_THICKNESS_MM / 2;
  const sideBottomY = baseY - 1.4;
  const leftFlangePath = `M 0 0
    H ${TVM_CONNECTOR_ARM_SIDE_LENGTH_MM - 3}
    L ${TVM_CONNECTOR_ARM_SIDE_LENGTH_MM - 2.2} ${TVM_CONNECTOR_ARM_TOP_THICKNESS_MM}
    H 0
    Z`;
  const rightFlangePath = `M ${TVM_CONNECTOR_WIDTH_MM - TVM_CONNECTOR_ARM_SIDE_LENGTH_MM + 2.2} ${TVM_CONNECTOR_ARM_TOP_THICKNESS_MM}
    L ${TVM_CONNECTOR_WIDTH_MM - TVM_CONNECTOR_ARM_SIDE_LENGTH_MM + 3} 0
    H ${TVM_CONNECTOR_WIDTH_MM}
    V ${TVM_CONNECTOR_ARM_TOP_THICKNESS_MM}
    Z`;
  const profilePath = `M 0 ${topY}
    H ${baseLeftX - 2.5}
    Q ${baseLeftX} ${topY} ${baseLeftX} ${bendY}
    V ${sideBottomY}
    Q ${baseLeftX} ${baseY} ${baseLeftX + TVM_CONNECTOR_PROFILE_THICKNESS_MM} ${baseY}
    H ${baseRightX - TVM_CONNECTOR_PROFILE_THICKNESS_MM}
    Q ${baseRightX} ${baseY} ${baseRightX} ${sideBottomY}
    V ${bendY}
    Q ${baseRightX} ${topY} ${baseRightX + 2.5} ${topY}
    H ${TVM_CONNECTOR_WIDTH_MM}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${TVM_CONNECTOR_WIDTH_MM} ${resolvedTotalHeight}`}
      aria-hidden="true"
      overflow="visible"
      shapeRendering="geometricPrecision"
      {...props}
    >
      {screw && (
        <KkScrewGraphic
          screw={screw}
          toPixels={millimetersToGraphicUnits}
          centerX={centerX}
          headBottomY={baseTopY}
        />
      )}
      <path
        d={leftFlangePath}
        fill={BODY_COLOR}
        stroke={STROKE_COLOR}
        strokeLinejoin="round"
        strokeWidth={FLANGE_STROKE_WIDTH}
      />
      <path
        d={rightFlangePath}
        fill={BODY_COLOR}
        stroke={STROKE_COLOR}
        strokeLinejoin="round"
        strokeWidth={FLANGE_STROKE_WIDTH}
      />
      <path
        d={profilePath}
        fill="none"
        stroke={STROKE_COLOR}
        strokeLinecap="butt"
        strokeLinejoin="round"
        strokeWidth={PROFILE_OUTLINE_WIDTH}
      />
      <path
        d={profilePath}
        fill="none"
        stroke={BODY_COLOR}
        strokeLinecap="butt"
        strokeLinejoin="round"
        strokeWidth={TVM_CONNECTOR_PROFILE_THICKNESS_MM}
      />
    </svg>
  );
};

export default Tvm;
